import { useEffect, useState, useRef } from "react";
import { extractAddressDetails, removeBlankFields, getPreferredAddress } from "../../Utilities/AddressManagementUtils";
import { GetADToken } from "../../Utilities/TokenManager";
import { APIGet } from "../../API/APIRequest";
import '../../../i18n';
import { useTranslation } from 'react-i18next';
import { gtmEventTriggers } from "../../Utilities/GtmFunctions";
import { isDeliveryCountryValid as isDeliveryCountryValidPure, buildLatestAddressBasketArgs } from "./latestAddressBasket";


export const LatestAddress = ({
  fid,
  type,
  setSelectedAddress,
  setSelectedCompany,
  setUsingLatest,
  usingLatest,
  setSectionComplete,
  handleOnChangeAddress,
  showAddressUI,
  showRecipientUI,
  setRecipientDetails,
  setGiftRecipientDetails,
  isGift,
  isPrint,
  collectBilling,
  optionalEmail,
  basketCountryCode,
  countryList,
  updateAndRefreshBasket,
  setLatestAddressCountryCode,
  // Delivery instance always owns the latest basket init. The billing instance
  // owns it only when delivery is not using a latest address (so latest billing
  // still reaches the basket). Defaults to true for the delivery instance.
  ownsLatestBasketInit = true

}) => {

  const ownsBasketInit =
    type === "delivery" || (type === "billing" && ownsLatestBasketInit);

  const adToken = GetADToken();
  const token = adToken?.token;
  const { t } = useTranslation();
  const hasFetched = useRef(false);
      const hasInitBasket = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [latestData, setLatestData] = useState(null);
  const isGiftDelivery = isGift && type === "delivery"


  
  useEffect(() => {
    if (!fid || !token || hasFetched.current) return;

    hasFetched.current = true;

    const fetchLatestAddress = async () => {

      setIsLoading(true)
      try {
        
        //retrieving the address from session storage if already fetched once in the session
        const cachedRaw = sessionStorage.getItem(`latestAddresses`);
        if (cachedRaw) {
          let cached = null;
          try { cached = JSON.parse(cachedRaw); } catch (_) { cached = null; }

          if (cached && cached.fid === fid) {
            setLatestData(cached);
            setIsLoading(false);
          
            return;
          }

          sessionStorage.removeItem("latestAddresses");
        }

        //else get the latest address from API
        const response = await APIGet({
          controller: "address",
          action: "getpartylocationoptions",
          data: { fid },
          headers: [{ key: "UserToken", value: token }]
        });

        const locations = response?.data?.locations;
        if (!locations?.length) {
          setUsingLatest(false);
          setIsLoading(false);
          return;
        }
        
        //processing the address to get the required fields for billing and delivery
        const data = {
          fid,
          billing: processAddress(locations, "billing"),
          delivery: processAddress(locations, "delivery")
        };

        sessionStorage.setItem(`latestAddresses`, JSON.stringify(data));
        setLatestData(data);
        setIsLoading(false);
  
      } catch (err) {

        setUsingLatest(false);
        setIsLoading(false);
      }
    };

    fetchLatestAddress();
  }, [fid, token]);

  
//assessing if latest address country is a match to basket country - if not don't show latest addrsss.
  const isDeliveryCountryValid = (deliveryAddress) =>
    isDeliveryCountryValidPure({ type, deliveryAddress, basketCountryCode, countryList });

  const makeLatestAddressBasketCall = (data) => {
    const args = buildLatestAddressBasketArgs({ type, data, basketCountryCode, countryList });
    if (args) {
      updateAndRefreshBasket(args.countryCode, args.type, args.result, args.countyCode);
    }
  };

  //to update the basket with right country codes and county codes for taxes when latest address is used for delivery or billing(or not)
  useEffect(() => {
    if (hasInitBasket.current) return;
    if (!ownsBasketInit || !updateAndRefreshBasket) return;
    if (!latestData) return;


    if (type === "delivery" && (!basketCountryCode || !countryList?.length)) return;

    if (type === "delivery" && !isDeliveryCountryValid(latestData?.delivery)) return;

    hasInitBasket.current = true;
    makeLatestAddressBasketCall(latestData);

  }, [latestData, basketCountryCode, countryList, ownsBasketInit, ownsLatestBasketInit, type]);

  const hasProcessed = useRef(false);

  const hasRejected = useRef(false);

  useEffect(() => {
    if (!latestData) return;

    // For delivery, wait for basketCountryCode before proceeding
    if (type === "delivery" && !basketCountryCode) return;

    const dataForType = latestData[type];
    if (!dataForType) {
      setIsLoading(false);
      if (!hasRejected.current) {
        hasRejected.current = true;
        setUsingLatest(false);
      }
      return;
    }

   
    if (type === "delivery" && dataForType?.country && basketCountryCode) {
      if (!isDeliveryCountryValid(dataForType)) {
        setIsLoading(false);
        if (!hasRejected.current) {
          hasRejected.current = true;
          setUsingLatest(false);
        }
        return;
      }
    }

    // For gift deliveries, we need to validate the recipient data before proceeding.
    if (isGiftDelivery) {
      const recipientValid = handleRecipientData(dataForType);
      if (!recipientValid) {
        setIsLoading(false);
        return;
      }
    }

    // Only run handleAddress once to avoid duplicate GTM events and state resets
    if (!hasProcessed.current) {
      handleAddress(dataForType);
      hasProcessed.current = true;
    }
    setIsLoading(false);
  }, [latestData, optionalEmail, basketCountryCode, countryList]);


// Function to process the address data and extract the necessary fields
 const processAddress = (locations, type) => {
    const preferred = getPreferredAddress(locations, { type, isGift, isPrint });
    if (!preferred) return null;
    
 
    const structured = extractAddressDetails(preferred);
    const cleaned = removeBlankFields(structured);

    return cleaned?.address || cleaned;
  };

// Function to handle recipient data for gift deliveries
 const handleRecipientData=(deliveryDetails)=>{

   const reciData = deliveryDetails;
 
  //email is mandatory in online and digital
   if((!reciData?.firstName || !reciData?.lastName || (!optionalEmail && !reciData?.email))){
 
      setRecipientDetails({firstName:"", lastName:"", userEmail:""})
      setGiftRecipientDetails({})
      setUsingLatest(false);
      return false;
   }

   setRecipientDetails({firstName:reciData?.firstName , lastName:reciData?.lastName, userEmail:reciData?.email})
   setGiftRecipientDetails({partyid:reciData?.partyId})
   return true;

 }

 //setting the address details in the state and triggering GTM event for shipping 
 const handleAddress = (address) => {
    if (!address?.country) {
      if(isGiftDelivery){
        setRecipientDetails({firstName:"", lastName:"", userEmail:""})
        setGiftRecipientDetails({})
      }
      
      setUsingLatest(false);
      return;
    }

    // Extract display fields only - exclude non-display fields from rest
    const { 
      companyName, 
      firstName, 
      lastName, 
      email, 
      partyId, 
      countyCode, 
      countryCode, 
      ...rest 
    } = address;


    if((isPrint && type === "delivery") || (collectBilling && type === "billing")){

    const formatted = Object.fromEntries(
      Object.entries(rest).map(([k, v]) => [k.toUpperCase(), v])
    );

    setSelectedAddress?.(formatted);

    if (companyName) {
      setSelectedCompany?.({ COMPANY: companyName, CompanyId: null });
    }

    // For preselecetion of delivery latest country when we change address. 
    if (type === "delivery" && countryList?.length) {
      const matchedCountry = countryList.find(
        c => c.countryname?.toLowerCase() === address.country.toLowerCase()
      );
      setLatestAddressCountryCode?.(matchedCountry?.countrycode);
    }

  }else{

    //online, digital and min billing
     setSelectedAddress?.({COUNTRY:address?.country})

    }

    setUsingLatest(true);
  
    if(type === "delivery"){
      setSectionComplete?.((prev) => ({
      ...prev,
      deliveryDetails: true
    }));

    //when using pre-existing address
    gtmEventTriggers("add_shipping_info");

    }
    
  };


  return (

    usingLatest &&
        <>
    {isLoading ? <div className="shimmer shimmer-input"></div> :
    <div className="address-block">
        
       <button onClick={handleOnChangeAddress}>
          { isGift ? t("checkout.changerecipientlabel")  :  t("checkout.changeaddresslabel")}
       </button>

       {type === "billing" ? (
         <div className="item-wrapper">
                        <div className="detail-wrapper">
                           
                            {showAddressUI()} 
                            
                        </div>
                    </div> 
                )
            
            :  isGift ?
            
            <div className="detail-wrapper">
              <div className="item-wrapper">
                <p><strong>{t("checkout.recipientlabel")}</strong></p>
                <p>{showRecipientUI()}</p>
              </div>


              <div className="item-wrapper">
                <p><strong>{t("checkout.addresslabel")}</strong></p>
                <p>{showAddressUI()}</p>
             </div>

            </div>
              
              :

              <div className="item-wrapper">
                <div className="detail-wrapper">
                  {showAddressUI()}
                </div>
              </div>

        }
       
      </div>}
      </> 
  );
};